import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Table,
  Input,
  Label,
  ButtonGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import moment from "moment";

// RESURTIDO INTELIGENTE
// Reporte de apoyo a compras. Por cada artículo calcula:
//   1. Demanda      = promedio de los últimos meses completos × multiplicador
//   2. Temporada    = una venta ajustada extra por cada mes pico (may/nov/dic)
//                     que caiga dentro del periodo de cobertura (prorrateado)
//   3. Stock total  = bodega + full + FBA + otros + tránsito + producción
//   4. Alerta       = stock total ≤ punto de recompra
//   5. Sugerencia   = max(0, meta − stock total)
// NO genera compras ni pagos: sólo informa para que alguien decida.
function ResurtidoInteligente() {
  const { user } = useContext(AuthContext);
  const URL_INVENTARIOS = process.env.REACT_APP_URL_INVENTARIOS;

  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // ── Parámetros (editables) ────────────────────────────────────────────────
  const [multiplicador, setMultiplicador] = useState(2);
  const [mesesHistoria, setMesesHistoria] = useState(3);
  const [diasProduccion, setDiasProduccion] = useState(60);
  const [diasTransito, setDiasTransito] = useState(30);
  const [diasSeguridad, setDiasSeguridad] = useState(30);
  const [mesesMeta, setMesesMeta] = useState(5);
  const [mesesPico, setMesesPico] = useState("5,11,12");
  const [produccionSinAnticipo, setProduccionSinAnticipo] = useState("No");
  const [incluirFullFBA, setIncluirFullFBA] = useState("Si");
  const [temporadaSoloConfirmada, setTemporadaSoloConfirmada] = useState("No");
  const [verParametros, setVerParametros] = useState(false);
  const [recargar, setRecargar] = useState(0);

  // ── Datos ─────────────────────────────────────────────────────────────────
  const [filas, setFilas] = useState([]);
  const [parametros, setParametros] = useState({});
  const [search, setSearch] = useState("");
  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [estado, setEstado] = useState("Comprar");

  const [modalDetalle, setModalDetalle] = useState(false);
  const [detalle, setDetalle] = useState(null);

  const hoy = moment().format("YYYY-MM-DD");

  const num = (n) => new Intl.NumberFormat("en-US").format(Math.round(Number(n) || 0));
  const dec = (n) =>
    new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(Number(n) || 0);
  const mxn = (n) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);

  useEffect(() => {
    showLoader();
    axios
      .get(`${URL_INVENTARIOS}Resurtido`, {
        params: {
          multiplicador,
          mesesHistoria,
          diasProduccion,
          diasTransito,
          diasSeguridad,
          mesesMeta,
          mesesPico,
          produccionSinAnticipo,
          incluirFullFBA,
          temporadaSoloConfirmada,
        },
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((r) => {
        setFilas(r.data?.articulos || []);
        setParametros(r.data?.parametros || {});
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        setFilas([]);
        hideLoader();
      });
  }, [recargar]);

  const proveedores = useMemo(() => {
    const set = new Set(filas.map((f) => f.proveedor).filter(Boolean));
    return Array.from(set).sort();
  }, [filas]);

  const datos = useMemo(() => {
    let arr = [...filas];
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (r) =>
          String(r.SKU || "").toLowerCase().includes(s) ||
          String(r.nombre || "").toLowerCase().includes(s) ||
          String(r.codigo || "").toLowerCase().includes(s) ||
          String(r.proveedor || "").toLowerCase().includes(s)
      );
    }
    if (selectedProveedor) arr = arr.filter((r) => r.proveedor === selectedProveedor);
    if (estado === "Comprar") arr = arr.filter((r) => r.estado === "Comprar");
    else if (estado === "Riesgo") arr = arr.filter((r) => r.riesgoQuiebre === "Si");
    else if (estado === "Exceso") arr = arr.filter((r) => r.estado === "Exceso");
    else if (estado === "ConVenta") arr = arr.filter((r) => r.ventaAjustada > 0);
    return arr;
  }, [filas, search, selectedProveedor, estado]);

  const resumen = useMemo(() => {
    const base = {
      comprar: 0,
      riesgo: 0,
      exceso: 0,
      piezas: 0,
      importe: 0,
      excedente: 0,
    };
    filas.forEach((r) => {
      if (r.estado === "Comprar") base.comprar++;
      if (r.riesgoQuiebre === "Si") base.riesgo++;
      if (r.estado === "Exceso") {
        base.exceso++;
        base.excedente += Number(r.excedente) || 0;
      }
      base.piezas += Number(r.sugeridoCajas) || 0;
      base.importe += Number(r.importeSugerido) || 0;
    });
    return base;
  }, [filas]);

  const totales = useMemo(
    () =>
      datos.reduce(
        (a, r) => ({
          stockTotal: a.stockTotal + (Number(r.stockTotal) || 0),
          sugerido: a.sugerido + (Number(r.sugeridoCajas) || 0),
          importe: a.importe + (Number(r.importeSugerido) || 0),
        }),
        { stockTotal: 0, sugerido: 0, importe: 0 }
      ),
    [datos]
  );

  const colorEstado = (e) => {
    if (e === "Comprar") return { color: "#b45309", fontWeight: 700 };
    if (e === "Exceso") return { color: "#1d4ed8", fontWeight: 700 };
    if (e === "Sin venta") return { color: "#94a3b8" };
    return { color: "#15803d", fontWeight: 700 };
  };

  function dataPlana() {
    return datos.map((r) => ({
      SKU: r.SKU || "",
      Articulo: r.nombre || "",
      Proveedor: r.proveedor || "",
      VentaPromedio: Number(r.ventaPromedio) || 0,
      VentaAjustada: Number(r.ventaAjustada) || 0,
      Temporada: Number(r.temporadaMeta) || 0,
      Bodega: Number(r.bodega) || 0,
      Full: Number(r.full) || 0,
      FBA: Number(r.fba) || 0,
      OtrosAlmacenes: Number(r.otrosAlmacenes) || 0,
      Transito: Number(r.transito) || 0,
      Produccion: Number(r.produccionCuenta) || 0,
      StockTotal: Number(r.stockTotal) || 0,
      PuntoRecompra: Number(r.puntoRecompra) || 0,
      Meta: Number(r.meta) || 0,
      Sugerido: Number(r.sugerido) || 0,
      SugeridoCajas: Number(r.sugeridoCajas) || 0,
      Estado: r.estado || "",
      RiesgoQuiebre: r.riesgoQuiebre || "No",
      ProximaLlegada: r.proximaLlegada || "",
      DiasCobertura: Number(r.diasCobertura) || 0,
      MinimoProduccion: Number(r.minimoProduccion) || 0,
      PiezasPorCaja: Number(r.piezasPorCaja) || 0,
      CostoUnitario: Number(r.costoUnitario) || 0,
      ImporteSugerido: Number(r.importeSugerido) || 0,
    }));
  }

  function PDFTabla() {
    const body = datos.map((r) => [
      r.SKU || "",
      (r.nombre || "").substring(0, 28),
      (r.proveedor || "").substring(0, 16),
      dec(r.ventaAjustada),
      num(r.stockTotal),
      num(r.puntoRecompra),
      num(r.meta),
      num(r.sugeridoCajas),
      mxn(r.importeSugerido),
      r.estado + (r.riesgoQuiebre === "Si" ? " - RIESGO" : ""),
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    const img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 8, 45, 15);
    const img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 125, 5, 30, 30);
    doc.setFontSize(14);
    doc.text(`Resurtido Inteligente`, 20, 16);
    doc.setFontSize(8);
    doc.text(
      `Venta ajustada = promedio de ${
        parametros.mesesHistoria || mesesHistoria
      } meses x ${parametros.multiplicador || multiplicador}`,
      20,
      23
    );
    doc.text(
      `Punto de recompra: ${diasProduccion}d prod + ${diasTransito}d transito + ${diasSeguridad}d seguridad`,
      20,
      27
    );
    doc.text(
      `Meta: ${parametros.mesesMeta || mesesMeta} meses + temporada  ·  Generado: ${
        parametros.generado || hoy
      }`,
      20,
      31
    );
    doc.autoTable({
      head: [
        [
          "SKU",
          "Artículo",
          "Proveedor",
          "Venta Aj.",
          "Stock Total",
          "Pto. Recompra",
          "Meta",
          "Sugerido",
          "Importe",
          "Estado",
        ],
      ],
      body,
      startY: 36,
      styles: { fontSize: 7 },
      headStyles: { fontSize: 7 },
      foot: [
        [
          "",
          "TOTAL",
          "",
          "",
          num(totales.stockTotal),
          "",
          "",
          num(totales.sugerido),
          mxn(totales.importe),
          "",
        ],
      ],
      footStyles: { fontSize: 8, fillColor: [255, 255, 255], textColor: [0, 0, 0] },
    });
    doc.save(`ResurtidoInteligente-${hoy}.pdf`);
  }

  function excel() {
    const option = {};
    option.fileName = `ResurtidoInteligente-${hoy}`;
    option.datas = [
      {
        sheetData: dataPlana(),
        sheetName: "Resurtido",
        sheetFilter: [
          "SKU",
          "Articulo",
          "Proveedor",
          "VentaPromedio",
          "VentaAjustada",
          "Temporada",
          "Bodega",
          "Full",
          "FBA",
          "OtrosAlmacenes",
          "Transito",
          "Produccion",
          "StockTotal",
          "PuntoRecompra",
          "Meta",
          "Sugerido",
          "SugeridoCajas",
          "Estado",
          "RiesgoQuiebre",
          "ProximaLlegada",
          "DiasCobertura",
          "MinimoProduccion",
          "PiezasPorCaja",
          "CostoUnitario",
          "ImporteSugerido",
        ],
        sheetHeader: [
          "SKU",
          "Artículo",
          "Proveedor",
          "Venta Prom. Mensual",
          "Venta Ajustada",
          "Temporada (pzs)",
          "Bodega",
          "Full",
          "FBA",
          "Otros Almacenes",
          "Tránsito",
          "Producción",
          "Stock Total",
          "Punto de Recompra",
          "Meta",
          "Sugerido",
          "Sugerido en Cajas",
          "Estado",
          "Riesgo de Quiebre",
          "Próxima Llegada",
          "Días de Cobertura",
          "Mínimo de Producción",
          "Piezas por Caja",
          "Costo Unitario",
          "Importe Sugerido",
        ],
      },
    ];
    new ExportJsonExcel(option).saveExcel();
  }

  function verDetalle(r) {
    setDetalle(r);
    setModalDetalle(true);
  }

  const Tarjeta = ({ titulo, valor, color, nota }) => (
    <Col md={2} xs={6} style={{ marginBottom: 8 }}>
      <div
        style={{
          border: "1px solid #e2e8f0",
          borderRadius: 10,
          padding: "8px 10px",
          background: "#f8fafc",
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1.1 }}>
          {valor}
        </div>
        <div
          style={{
            fontSize: 10,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: ".04em",
          }}
        >
          {titulo}
        </div>
        {nota ? (
          <div style={{ fontSize: 10, color: "#94a3b8" }}>{nota}</div>
        ) : null}
      </div>
    </Col>
  );

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuCompras"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i class="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i class="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Resurtido Inteligente</h3>
          <div align="center" style={{ fontSize: 12, color: "#64748b" }}>
            Demanda ajustada + temporada contra el stock total (bodega, Full, FBA,
            tránsito y producción). El sistema sólo avisa y sugiere:{" "}
            <b>no genera compras ni pagos</b>.
          </div>
          <br />

          {/* ── Resumen ── */}
          <Row>
            <Tarjeta
              titulo="Hay que comprar"
              valor={num(resumen.comprar)}
              color="#b45309"
              nota="stock ≤ punto de recompra"
            />
            <Tarjeta
              titulo="Riesgo de quiebre"
              valor={num(resumen.riesgo)}
              color="#dc2626"
              nota="antes de la próxima llegada"
            />
            <Tarjeta
              titulo="En exceso"
              valor={num(resumen.exceso)}
              color="#1d4ed8"
              nota={`${num(resumen.excedente)} pzs sobre la meta`}
            />
            <Tarjeta
              titulo="Piezas sugeridas"
              valor={num(resumen.piezas)}
              color="#0f172a"
            />
            <Tarjeta
              titulo="Importe estimado"
              valor={mxn(resumen.importe)}
              color="#0f172a"
              nota="a costo actual"
            />
            <Tarjeta
              titulo="Artículos analizados"
              valor={num(filas.length)}
              color="#0f172a"
              nota={parametros.generado || ""}
            />
          </Row>

          {/* ── Parámetros ── */}
          <Row className="align-items-end">
            <Col md={12}>
              <Button
                size="sm"
                color="link"
                onClick={() => setVerParametros(!verParametros)}
                style={{ paddingLeft: 0 }}
              >
                {verParametros ? "Ocultar" : "Ver"} parámetros del cálculo{" "}
                <i class="fas fa-sliders-h"></i>
              </Button>
            </Col>
          </Row>
          {verParametros ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                padding: 12,
                marginBottom: 12,
                background: "#f8fafc",
              }}
            >
              <Row className="align-items-end">
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Multiplicador
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    step="any"
                    min="0"
                    value={multiplicador}
                    onChange={(e) => setMultiplicador(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Meses de historia
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    min="1"
                    value={mesesHistoria}
                    onChange={(e) => setMesesHistoria(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Días de producción
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    min="0"
                    value={diasProduccion}
                    onChange={(e) => setDiasProduccion(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Días de tránsito
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    min="0"
                    value={diasTransito}
                    onChange={(e) => setDiasTransito(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Días de seguridad
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    min="0"
                    value={diasSeguridad}
                    onChange={(e) => setDiasSeguridad(e.target.value)}
                  />
                </Col>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Meses meta
                  </Label>
                  <Input
                    bsSize="sm"
                    type="number"
                    step="any"
                    min="1"
                    value={mesesMeta}
                    onChange={(e) => setMesesMeta(e.target.value)}
                  />
                </Col>
              </Row>
              <Row className="align-items-end" style={{ marginTop: 8 }}>
                <Col md={2}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Meses pico
                  </Label>
                  <Input
                    bsSize="sm"
                    type="text"
                    value={mesesPico}
                    onChange={(e) => setMesesPico(e.target.value)}
                  />
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>
                    Número de mes separado por coma (5=mayo). Vacío = sin
                    temporada
                  </div>
                </Col>
                <Col md={3}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Contar producción sin anticipo
                  </Label>
                  <Input
                    bsSize="sm"
                    type="select"
                    value={produccionSinAnticipo}
                    onChange={(e) => setProduccionSinAnticipo(e.target.value)}
                  >
                    <option value="No">No (sólo con anticipo pagado)</option>
                    <option value="Si">Sí (toda la OC pendiente)</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Contar Full y FBA como stock
                  </Label>
                  <Input
                    bsSize="sm"
                    type="select"
                    value={incluirFullFBA}
                    onChange={(e) => setIncluirFullFBA(e.target.value)}
                  >
                    <option value="Si">Sí (bodega + Full + FBA)</option>
                    <option value="No">No (sólo bodega)</option>
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="mr-sm-2" style={{ fontSize: 12 }}>
                    Temporada
                  </Label>
                  <Input
                    bsSize="sm"
                    type="select"
                    value={temporadaSoloConfirmada}
                    onChange={(e) => setTemporadaSoloConfirmada(e.target.value)}
                  >
                    <option value="No">Aplicar a todos los artículos</option>
                    <option value="Si">
                      Sólo si el histórico la confirma
                    </option>
                  </Input>
                </Col>
                <Col md={2}>
                  <Button
                    size="sm"
                    color="success"
                    onClick={() => setRecargar(recargar + 1)}
                  >
                    Calcular <i class="fas fa-sync"></i>
                  </Button>
                </Col>
              </Row>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 8 }}>
                Punto de recompra = (días de producción + tránsito + seguridad) de
                venta ajustada + temporada. Si el artículo tiene capturados sus
                días de producción, se usan los suyos. La meta nunca queda por
                debajo del punto de recompra.
              </div>
            </div>
          ) : null}

          {/* ── Filtros ── */}
          <Row className="align-items-end">
            <Col md={3}>
              <Label className="mr-sm-2">Buscar</Label>
              <Input
                bsSize="sm"
                type="text"
                placeholder="SKU, artículo o proveedor"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Proveedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedProveedor}
                onChange={(e) => setSelectedProveedor(e.target.value)}
              >
                <option value="">Todos</option>
                {proveedores.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label className="mr-sm-2">Mostrar</Label>
              <Input
                bsSize="sm"
                type="select"
                value={estado}
                onChange={(e) => setEstado(e.target.value)}
              >
                <option value="Comprar">Sólo los que hay que comprar</option>
                <option value="Riesgo">Sólo riesgo de quiebre</option>
                <option value="Exceso">Sólo excesos</option>
                <option value="ConVenta">Sólo con venta</option>
                <option value="Todos">Todos</option>
              </Input>
            </Col>
            <Col md={3}>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                {num(datos.length)} artículos en pantalla
              </div>
            </Col>
          </Row>
          <br />

          <Table size="sm" borderless responsive>
            <thead>
              <tr>
                <th style={{ paddingBottom: "0px" }}>SKU</th>
                <th style={{ paddingBottom: "0px" }}>Artículo</th>
                <th style={{ paddingBottom: "0px" }}>Proveedor</th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Venta Aj.
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Bodega
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Full
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  FBA
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Tránsito
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Producción
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Stock Total
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Pto. Recompra
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Meta
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Sugerido
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Importe
                </th>
                <th style={{ paddingBottom: "0px" }} align="center">
                  Estado
                </th>
                <th style={{ paddingBottom: "0px" }}></th>
              </tr>
            </thead>
            <tbody>
              {datos.map((r) => (
                <tr key={r.idArticulo}>
                  <td style={{ fontSize: "12px" }}>{r.SKU}</td>
                  <td style={{ fontSize: "12px" }}>{r.nombre}</td>
                  <td style={{ fontSize: "12px" }}>{r.proveedor}</td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {dec(r.ventaAjustada)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.bodega)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.full)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.fba)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.transito)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.produccionCuenta)}
                  </td>
                  <td style={{ fontSize: "12px", fontWeight: 700 }} align="center">
                    {num(r.stockTotal)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.puntoRecompra)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {num(r.meta)}
                  </td>
                  <td
                    style={{ fontSize: "12px", fontWeight: 700 }}
                    align="center"
                  >
                    {num(r.sugeridoCajas)}
                  </td>
                  <td style={{ fontSize: "12px" }} align="center">
                    {r.sugeridoCajas > 0 ? mxn(r.importeSugerido) : ""}
                  </td>
                  <td
                    style={{ fontSize: "12px", ...colorEstado(r.estado) }}
                    align="center"
                  >
                    {r.estado}
                    {r.riesgoQuiebre === "Si" ? (
                      <span
                        title="Se acaba antes de la próxima llegada"
                        style={{ color: "#dc2626" }}
                      >
                        {" "}
                        <i class="fas fa-exclamation-triangle"></i>
                      </span>
                    ) : null}
                  </td>
                  <td align="center">
                    <Button
                      size="sm"
                      color="info"
                      onClick={() => verDetalle(r)}
                      title="Ver cómo se calculó"
                    >
                      <i class="fas fa-search"></i>
                    </Button>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "2px solid #e2e8f0" }}>
                <td style={{ fontSize: "13px", fontWeight: 700 }}>TOTAL</td>
                <td colSpan={8}></td>
                <td style={{ fontSize: "13px", fontWeight: 700 }} align="center">
                  {num(totales.stockTotal)}
                </td>
                <td colSpan={2}></td>
                <td style={{ fontSize: "13px", fontWeight: 700 }} align="center">
                  {num(totales.sugerido)}
                </td>
                <td style={{ fontSize: "13px", fontWeight: 700 }} align="center">
                  {mxn(totales.importe)}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </Table>
        </div>
      ) : undefined}

      {/* ── Detalle del cálculo ── */}
      <Modal
        size="lg"
        isOpen={modalDetalle}
        toggle={() => setModalDetalle(!modalDetalle)}
      >
        <ModalHeader toggle={() => setModalDetalle(!modalDetalle)}>
          {detalle ? `${detalle.SKU} · ${detalle.nombre}` : ""}
        </ModalHeader>
        <ModalBody>
          {detalle ? (
            <>
              <h6>1. Demanda</h6>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td>Venta de los últimos {mesesHistoria} meses completos</td>
                    <td align="right">
                      {(detalle.mesesHistoriaLista || []).map((m, i) => (
                        <span key={m} style={{ marginLeft: 8 }}>
                          {m}: <b>{num((detalle.ventaPorMes || [])[i])}</b>
                        </span>
                      ))}
                    </td>
                  </tr>
                  <tr>
                    <td>Promedio mensual</td>
                    <td align="right">{dec(detalle.ventaPromedio)} pzs</td>
                  </tr>
                  <tr>
                    <td>
                      × multiplicador {detalle.multiplicador} ={" "}
                      <b>venta mensual ajustada</b>
                    </td>
                    <td align="right">
                      <b>{dec(detalle.ventaAjustada)} pzs</b>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b" }}>
                      Mes en curso (informativo, no entra al promedio)
                    </td>
                    <td align="right" style={{ color: "#64748b" }}>
                      {num(detalle.ventaMesActual)} pzs
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h6>2. Temporada</h6>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td>Meses pico dentro de la cobertura meta</td>
                    <td align="right">{dec(detalle.mesesPicoMeta)}</td>
                  </tr>
                  <tr>
                    <td>Piezas extra por temporada</td>
                    <td align="right">{num(detalle.temporadaMeta)} pzs</td>
                  </tr>
                  <tr>
                    <td>
                      ¿El histórico de 12 meses confirma la temporada de este
                      artículo?
                    </td>
                    <td align="right">
                      {detalle.temporadaConfirmada === "Si" ? (
                        <span style={{ color: "#15803d", fontWeight: 700 }}>
                          Sí (índice {dec(detalle.indiceTemporada)}×)
                        </span>
                      ) : (
                        <span style={{ color: "#b45309", fontWeight: 700 }}>
                          No (índice {dec(detalle.indiceTemporada)}×)
                        </span>
                      )}
                    </td>
                  </tr>
                  {detalle.temporadaAplicada === "No" ? (
                    <tr>
                      <td colSpan={2} style={{ color: "#64748b" }}>
                        No se aplicó temporada a este artículo por el filtro
                        elegido.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>

              <h6>3. Stock total (sin duplicar)</h6>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td>
                      Bodega / Full / FBA / otros almacenes
                      {incluirFullFBA === "No" ? (
                        <span style={{ color: "#64748b" }}>
                          {" "}
                          (Full y FBA no cuentan)
                        </span>
                      ) : null}
                    </td>
                    <td align="right">
                      {num(detalle.bodega)} / {num(detalle.full)} /{" "}
                      {num(detalle.fba)} / {num(detalle.otrosAlmacenes)}
                    </td>
                  </tr>
                  <tr>
                    <td>Tránsito (embarcado, sin recibir)</td>
                    <td align="right">{num(detalle.transito)} pzs</td>
                  </tr>
                  <tr>
                    <td>Producción (OC con anticipo pagado)</td>
                    <td align="right">{num(detalle.produccion)} pzs</td>
                  </tr>
                  <tr>
                    <td>Producción que se sumó al stock</td>
                    <td align="right">{num(detalle.produccionCuenta)} pzs</td>
                  </tr>
                  <tr>
                    <td style={{ color: "#64748b" }}>
                      OC sin anticipo{" "}
                      {produccionSinAnticipo === "Si"
                        ? "(incluida en el stock)"
                        : "(no cuenta como stock)"}
                    </td>
                    <td align="right" style={{ color: "#64748b" }}>
                      {num(detalle.produccionSinAnticipo)} pzs
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Stock total</b>
                    </td>
                    <td align="right">
                      <b>{num(detalle.stockTotal)} pzs</b>
                    </td>
                  </tr>
                  <tr>
                    <td>Disponible hoy (sin tránsito ni producción)</td>
                    <td align="right">
                      {num(detalle.stockPropio)} pzs ·{" "}
                      {num(detalle.diasCobertura)} días de cobertura
                    </td>
                  </tr>
                  <tr>
                    <td>Próxima llegada</td>
                    <td align="right">
                      {detalle.proximaLlegada ? (
                        <>
                          {detalle.proximaLlegada}{" "}
                          {detalle.diasParaLlegada < 0 ? (
                            <span style={{ color: "#dc2626" }}>
                              (vencida hace {Math.abs(detalle.diasParaLlegada)}{" "}
                              días)
                            </span>
                          ) : (
                            <>(en {detalle.diasParaLlegada} días)</>
                          )}
                        </>
                      ) : (
                        "Sin embarques pendientes"
                      )}
                    </td>
                  </tr>
                  {detalle.riesgoQuiebre === "Si" ? (
                    <tr>
                      <td colSpan={2} style={{ color: "#dc2626", fontWeight: 700 }}>
                        Se acaba antes de que llegue el siguiente embarque.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>

              <h6>4. Punto de recompra y meta</h6>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td>
                      Cobertura de recompra ({detalle.diasProduccionArt} días de
                      producción + {diasTransito} de tránsito + {diasSeguridad} de
                      seguridad)
                    </td>
                    <td align="right">{dec(detalle.mesesRecompra)} meses</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Punto de recompra</b> (con temporada)
                    </td>
                    <td align="right">
                      <b>{num(detalle.puntoRecompra)} pzs</b>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>Meta</b> ({dec(detalle.mesesMeta)} meses + temporada)
                    </td>
                    <td align="right">
                      <b>{num(detalle.meta)} pzs</b>
                    </td>
                  </tr>
                </tbody>
              </Table>

              <h6>5. Sugerencia</h6>
              <Table size="sm" borderless>
                <tbody>
                  <tr>
                    <td>
                      Meta {num(detalle.meta)} − stock total{" "}
                      {num(detalle.stockTotal)}
                    </td>
                    <td align="right">{num(detalle.sugeridoBase)} pzs</td>
                  </tr>
                  <tr>
                    <td>
                      Ajustado al mínimo de producción (
                      {num(detalle.minimoProduccion)}) y a cajas de{" "}
                      {num(detalle.piezasPorCaja)}
                    </td>
                    <td align="right">
                      <b>{num(detalle.sugeridoCajas)} pzs</b>
                    </td>
                  </tr>
                  <tr>
                    <td>Importe estimado (costo {mxn(detalle.costoUnitario)})</td>
                    <td align="right">
                      <b>{mxn(detalle.importeSugerido)}</b>
                    </td>
                  </tr>
                  {detalle.excedente > 0 ? (
                    <tr>
                      <td style={{ color: "#1d4ed8" }}>Excedente sobre la meta</td>
                      <td align="right" style={{ color: "#1d4ed8" }}>
                        {num(detalle.excedente)} pzs
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
              <div style={{ fontSize: 12, color: "#64748b" }}>
                Antes de aprobar: revisar mínimos del proveedor, cajas,
                presupuesto y espacio en almacén. El sistema no genera compras ni
                pagos automáticos.
              </div>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalDetalle(false)}>
            Cerrar
          </Button>
        </ModalFooter>
      </Modal>
      {loader}
    </>
  );
}

export default ResurtidoInteligente;
